import { ProceduralPoolGrid, PROC_SURFACE_IDS } from '../../../../Pool/logic/ProceduralPoolGrid';
import { getNozzleWallMountWorldY, getVacuumNozzleWallMountWorldY } from '../../../../utils/poolGeometryUiState';
import { getNozzleModelParams } from './positioning';
import { NOZZLE_CONFIG } from './config';
import { NOZZLE_TYPE } from './constants';
import { getNozzleMountType } from './mountType';

/** Доля длины стены от края, где ставим пылесосную форсунку — ближе к
 * одному краю стены, не по центру (в скиммерном бассейне скиммеры всегда
 * на этой же стене, см. skimmers/logic/core.js — форсунка встаёт рядом с
 * ними; в переливном отдельного скиммера нет, но формула стены та же). */
const VACUUM_NEAR_END_FRACTION = 0.12;

/**
 * Стена для пылесосной форсунки — длинная сторона чаши, ближняя к
 * техническому помещению. Техпомещение всегда пристроено с +X (см.
 * TechnicalRoom/hooks/useRoomDimensions.js: baseX = +effectiveWidth/2 + ...),
 * поэтому когда длинные стены идут по оси X (length>=width — WALL_LEFT/
 * WALL_RIGHT), берём именно WALL_RIGHT (она на +X, у техпомещения). Для
 * скиммерного бассейна это ровно та же стена, где стоят скиммеры
 * (skimmers/logic/core.js использует идентичную формулу).
 */
function getVacuumNozzleWallId(length, width) {
    const isLengthMain = length >= width;
    return isLengthMain ? PROC_SURFACE_IDS.WALL_RIGHT : PROC_SURFACE_IDS.WALL_FRONT;
}

/** Противоположная стена — для второй пылесосной форсунки (владелец:
 * при площади 100–625 м² их две, вторая на противоположной стороне). */
function getOppositeWallId(surfaceId) {
    switch (surfaceId) {
        case PROC_SURFACE_IDS.WALL_RIGHT: return PROC_SURFACE_IDS.WALL_LEFT;
        case PROC_SURFACE_IDS.WALL_LEFT: return PROC_SURFACE_IDS.WALL_RIGHT;
        case PROC_SURFACE_IDS.WALL_FRONT: return PROC_SURFACE_IDS.WALL_BACK;
        case PROC_SURFACE_IDS.WALL_BACK: return PROC_SURFACE_IDS.WALL_FRONT;
        default: return null;
    }
}

/** Один слот пылесосной форсунки на заданной стене, ближе к одному краю. */
function calculateVacuumNozzleSlot(gridSystem, nozzleWallY, surfaceId) {
    const surface = gridSystem.surfaces[surfaceId];
    if (!surface) return null;

    const posMain = -surface.sizeMain / 2 + VACUUM_NEAR_END_FRACTION * surface.sizeMain;

    const point = {
        x: 0,
        y: nozzleWallY,
        z: 0,
        rotation: surface.rotation,
        normal: surface.normal,
        surfaceId: surface.id
    };

    if (surface.variableAxis === 'z') {
        point.z = posMain;
        point.x = surface.constantCoord;
    } else {
        point.x = posMain;
        point.z = surface.constantCoord;
    }

    point.position = [point.x, point.y, point.z];
    return point;
}

/**
 * Рассчитать слоты для форсунок (стеновые / донные / пылесосные).
 *
 * Стена/дно у ОСНОВНЫХ форсунок (не пылесосной) — чисто геометрическая
 * эвристика по количеству (60/40 при quantity>6), НЕ зависит от товара:
 * владелец подтвердил, что в скиммерном бассейне выбирается только
 * стеновая МОДЕЛЬ, но часть инстансов при больших объёмах всё равно
 * физически стоит на дне (это только визуальное распределение) — донная
 * МОДЕЛЬ в скиммерном бассейне вообще не появляется в списке выбора
 * (см. server/nozzles/catalog.js filterNozzleItems), поэтому здесь не
 * нужно смотреть mountType для этой ветки.
 *
 * Пылесосная — единственный случай, где тип монтажа реально меняет
 * расстановку: на стене (не по общей формуле), а не в общей ветке — и это
 * ТАК ЖЕ для обоих типов бассейна (владелец: "пылесосные форсунки должны
 * быть на стенах" — для скиммерного и для переливного, даже несмотря на
 * то, что все ОСТАЛЬНЫЕ форсунки переливного бассейна на дне). Количество
 * зависит от площади зеркала воды (см. server nozzles/logic.js
 * resolveVacuumNozzleQuantity): 1 шт. до 100 м², 2 шт. 100–625 м² — вторая
 * на противоположной стене. Высота — 30 см от борта (не 35, как у обычных
 * стеновых форсунок, см. getVacuumNozzleWallMountWorldY).
 *
 * @param {object} poolDimensions
 * @param {number} quantity
 * @param {string} poolType
 * @param {object|null} nozzle — выбранный товар (только для определения пылесосной)
 */
export const calculateNozzleSlots = (poolDimensions, quantity = 1, poolType = 'скиммерный', nozzle = null) => {
    const { length, width, depth } = poolDimensions;
    const gridSystem = new ProceduralPoolGrid(length, width, depth, poolType);

    const normalizedPoolType = (poolType || '').toLowerCase();
    const isOverflow = normalizedPoolType === 'переливной' || normalizedPoolType === 'overflow';

    const mountType = getNozzleMountType(nozzle);
    const nozzleWallY = getNozzleWallMountWorldY(depth);

    if (mountType === NOZZLE_TYPE.VACUUM) {
        const vacuumWallY = getVacuumNozzleWallMountWorldY(depth);
        const primaryWallId = getVacuumNozzleWallId(length, width);
        const vacuumQuantity = Math.max(1, Math.min(2, Math.round(quantity) || 1));

        const vacuumSlots = [];
        const primary = calculateVacuumNozzleSlot(gridSystem, vacuumWallY, primaryWallId);
        if (primary) {
            vacuumSlots.push({ ...primary, side: primary.surfaceId, groupType: NOZZLE_TYPE.VACUUM, id: 'nozzle-vacuum-0', index: 0 });
        }
        if (vacuumQuantity >= 2) {
            const oppositeWallId = getOppositeWallId(primaryWallId);
            const secondary = oppositeWallId ? calculateVacuumNozzleSlot(gridSystem, vacuumWallY, oppositeWallId) : null;
            if (secondary) {
                vacuumSlots.push({ ...secondary, side: secondary.surfaceId, groupType: NOZZLE_TYPE.VACUUM, id: 'nozzle-vacuum-1', index: 1 });
            }
        }
        if (vacuumSlots.length) return vacuumSlots;
    }

    let wallQuantity = quantity;
    let bottomQuantity = 0;
    const useTwoWalls = false;

    if (isOverflow) {
        wallQuantity = 0;
        bottomQuantity = quantity;
    } else if (quantity > 6) {
        wallQuantity = Math.round(quantity * 0.60);
        bottomQuantity = quantity - wallQuantity;
    }

    const allSlots = [];

    // --- 1. СТЕНЫ ---
    if (wallQuantity > 0) {
        const isLengthMain = length >= width;
        const primaryWallId = isLengthMain ? PROC_SURFACE_IDS.WALL_LEFT : PROC_SURFACE_IDS.WALL_BACK;
        const secondaryWallId = isLengthMain ? PROC_SURFACE_IDS.WALL_RIGHT : PROC_SURFACE_IDS.WALL_FRONT;
        const wallDistributeOpts = { padding: 0, fixedY: nozzleWallY };

        if (useTwoWalls) {
            const q1 = Math.ceil(wallQuantity / 2);
            const q2 = wallQuantity - q1;

            const slots1 = gridSystem.distributeEvenly(primaryWallId, q1, wallDistributeOpts);
            slots1.forEach(s => allSlots.push({ ...s, side: primaryWallId, groupType: NOZZLE_TYPE.WALL }));

            const slots2 = gridSystem.distributeEvenly(secondaryWallId, q2, wallDistributeOpts);
            slots2.forEach(s => allSlots.push({ ...s, side: secondaryWallId, groupType: NOZZLE_TYPE.WALL }));

        } else {
            const slots = gridSystem.distributeEvenly(primaryWallId, wallQuantity, wallDistributeOpts);
            slots.forEach(s => allSlots.push({ ...s, side: primaryWallId, groupType: NOZZLE_TYPE.WALL }));
        }
    }

    // --- 2. ДНО ---
    if (bottomQuantity > 0) {
        const floorSlots = gridSystem.distributeOnFloor(bottomQuantity);
        floorSlots.forEach((s) =>
            allSlots.push({
                ...s,
                side: PROC_SURFACE_IDS.FLOOR,
                groupType: NOZZLE_TYPE.BOTTOM,
            }),
        );
    }

    return allSlots.map((slot, i) => ({
        ...slot,
        id: `nozzle-${i}`,
        index: i
    }));
};

/**
 * Рассчитать финальные позиции для визуализации
 */
export const calculateNozzlePositions = (poolDimensions, quantity, poolType, nozzle = null) => {
    const slots = calculateNozzleSlots(poolDimensions, quantity, poolType, nozzle);
    const { depth } = poolDimensions;
    const bottomYOffset = NOZZLE_CONFIG.POSITIONING.BOTTOM_Y_OFFSET ?? 0;

    return slots.map(slot => {
        const isBottom = slot.groupType === NOZZLE_TYPE.BOTTOM;

        // 1. Базовые координаты
        let { x, y, z, rotation, normal } = slot;

        // 2. Масштаб и оффсеты от стены
        const { scale, wallOffset, heightCompensation } = getNozzleModelParams(null, depth);

        // 3. Смещения (Offset)
        if (!isBottom) {
            x -= normal[0] * wallOffset;
            z -= normal[2] * wallOffset;
            // Y: 35 см ниже верхнего края чаши (getNozzleWallMountWorldY + fixedY в сетке).
            y += heightCompensation;

        } else {
            // Для донных форсунок применяем отдельную коррекцию по Y,
            // чтобы модель не "висела" над дном из-за pivot/геометрии GLB.
            y += bottomYOffset;
        }

        return {
            ...slot,
            x, y, z,
            position: [x, y, z],
            rotation,
            scale,
            nozzle,
            groupType: slot.groupType
        };
    });
};
